/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo, useState } from 'react';

import { jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';

import { withIntlProvider } from '../../../services';
import type { RenderableMenuItem, RenderableMenuSectionData } from '../../../types/menuV2';
import MenuItemV2 from '../MenuItemV2';
import MenuSectionPrimitive from '../MenuSectionPrimitive';
import { getFoldableThreshold } from '../MenuSectionPrimitive/utils';

const LEGACY_MAX_ITEMS = 10;
const OVERFLOW_MAX_ITEMS = 50;

export interface RenderableMenuSectionProps {
	section: RenderableMenuSectionData;
	sectionIndex: number;
	sectionStartIndex: number;
	totalMenuItemsCount: number;
}

function RenderableMenuSectionInner({
	section,
	sectionIndex,
	sectionStartIndex,
	totalMenuItemsCount,
}: RenderableMenuSectionProps) {
	const [showAll, setShowAll] = useState(false);

	const sectionItems = section.items;
	const sectionItemsCount = sectionItems.length;

	const items = useMemo(() => {
		if (!sectionItemsCount) {
			return [];
		}

		if (!section.foldable || sectionItemsCount <= getFoldableThreshold(section.foldable)) {
			return sectionItems;
		}

		const sectionItemsLimit = fg('navx_universal_create_overflow_mode')
			? OVERFLOW_MAX_ITEMS
			: LEGACY_MAX_ITEMS;

		return sectionItems.slice(
			0,
			showAll ? sectionItemsLimit : getFoldableThreshold(section.foldable),
		);
	}, [section.foldable, sectionItems, showAll, sectionItemsCount]);

	const toggleShowMore = useCallback(() => {
		if (typeof section.foldable === 'number' && fg('navx-2610-uc-refactor-show-more')) {
			setShowAll(true); // removing show less functionality under this fg
		} else {
			setShowAll((prev) => !prev);
		}
	}, [setShowAll, section.foldable]);

	if (!sectionItems?.length) {
		return null;
	}

	return (
		<MenuSectionPrimitive
			sectionIndex={sectionIndex}
			itemLength={sectionItems.length}
			foldable={section.foldable}
			heading={section.heading}
			showAll={showAll}
			toggleShowMore={toggleShowMore}
			isNative
		>
			{items.map((menuItem: RenderableMenuItem, index: number) => (
				<MenuItemV2
					item={menuItem}
					key={`menu-section-${sectionIndex}-item-${index}`}
					itemIndex={index}
					sectionStartIndex={sectionStartIndex}
					sectionItemsCount={sectionItemsCount}
					totalMenuItemsCount={totalMenuItemsCount}
				/>
			))}
		</MenuSectionPrimitive>
	);
}

export const RenderableMenuSection = withIntlProvider(RenderableMenuSectionInner);
